import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";

const CartCtx = createContext(null);

const STORAGE_KEY = "ipv_cart_v2";

export const itemKey = (product_id, color) => `${product_id}::${color || ""}`;

// Walmart-style quantity deal: returns {total, savings, dealActive}
export const lineTotals = (item) => {
  const base = item.price * item.quantity;
  const d = item.bulk_deal;
  if (d && d.min_qty >= 2 && d.percent_off > 0 && item.quantity >= d.min_qty) {
    const savings = +((base * d.percent_off) / 100).toFixed(2);
    return { total: +(base - savings).toFixed(2), savings, dealActive: true };
  }
  return { total: +base.toFixed(2), savings: 0, dealActive: false };
};

// Returns true if a cart item is excluded from the 10% Member Discount
// (Build-a-Pallet AND Closeout & Overstock Pallets are excluded per business rule).
export const isPalletItem = (item) =>
  item.product_id === "PALLET_BUILDER" || !!item.is_pallet;

// Calculate 10% member savings on non-pallet line totals (only for signed-in users).
export const memberSavings = (items, isMember) => {
  if (!isMember) return 0;
  const eligible = items.reduce((s, i) => {
    if (isPalletItem(i)) return s;
    return s + lineTotals(i).total;
  }, 0);
  return +(eligible * 0.10).toFixed(2);
};

export function CartProvider({ children }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (product, quantity = 1, color = null, swapRequest = null) => {
    if (product?.category === "Adult (18+)" || product?.category === "Tobacco Accessories") {
      toast.error("This item checks out on our secure partner store — use the checkout button on the product page.");
      return;
    }
    const key = itemKey(product.id, color);
    setItems((prev) => {
      const found = prev.find((i) => i.key === key);
      if (found) {
        return prev.map((i) =>
          i.key === key
            ? { ...i, quantity: i.quantity + quantity, swap_request: swapRequest || i.swap_request || null }
            : i
        );
      }
      return [
        ...prev,
        {
          key,
          product_id: product.id,
          name: product.name,
          price: product.price,
          image: (product.images || [])[0],
          stock: product.stock,
          color,
          fulfillment_type: product.fulfillment_type || "in_stock",
          lead_time_days: product.lead_time_days || null,
          bulk_deal: product.bulk_deal || null,
          is_pallet: !!product.is_pallet,
          ships_only: !!product.ships_only,
          swap_request: swapRequest || null,
          quantity,
        },
      ];
    });
    setDrawerOpen(true);
  };

  const addPallet = (tier, fee, productIds, contentNames) => {
    const customizationFee = Number(fee || 0);
    setItems((prev) => [
      ...prev,
      {
        key: `pallet::${Date.now()}`,
        product_id: "PALLET_BUILDER",
        name: `${tier.item_count}-Item Custom Pallet — merchandise included`,
        price: tier.price + customizationFee,
        pallet_price: tier.price,
        customization_fee: customizationFee,
        image: null,
        stock: 999,
        color: null,
        fulfillment_type: "in_stock",
        lead_time_days: null,
        bulk_deal: null,
        quantity: 1,
        pallet_tier: tier.item_count,
        pallet_items: productIds,
        pallet_names: contentNames,
      },
    ]);
  };

  const updateQty = (key, quantity) => {
    if (quantity <= 0) return removeItem(key);
    setItems((prev) =>
      prev.map((i) => (i.key === key ? { ...i, quantity } : i))
    );
  };

  const removeItem = (key) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
  };

  const clear = () => setItems([]);

  const subtotal = +items.reduce((s, i) => s + lineTotals(i).total, 0).toFixed(2);
  const savings = +items.reduce((s, i) => s + lineTotals(i).savings, 0).toFixed(2);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartCtx.Provider
      value={{ items, addItem, addPallet, updateQty, removeItem, clear, subtotal, savings, count, drawerOpen, setDrawerOpen }}
    >
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
